/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import {
  Form, Input, Col, Row, Modal, Button, Spin, Checkbox, Tag, message,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import Uploader from 'components/Uploader';
import { getEmpleadosConDatosOpFilteredPaginated } from 'api/estructuraOrganizacional/empleados';

const LOTE_CONCURRENCIA = 5;

/** Extrae de un archivo CSV/XLSX los valores de la primera columna que
 * parezcan un número de empleado (solo dígitos); descarta filas vacías,
 * encabezados de texto y duplicados. */
const extraerNumerosDeArchivo = async (file) => {
  const esCsv = file.name.split('.').pop().toLowerCase() === 'csv';
  const datos = await file.arrayBuffer();
  const workbook = XLSX.read(datos, { type: 'array', raw: esCsv });
  const hoja = workbook.Sheets[workbook.SheetNames[0]];
  const filas = XLSX.utils.sheet_to_json(hoja, { header: 1, defval: '' });
  const numeros = filas
    .map((fila) => String(fila?.[0] ?? '').trim())
    .filter((valor) => /^\d+$/.test(valor));
  return [...new Set(numeros)];
};

const baseURIDatosOp = 'catalogos/dato-operacional-del-empleado-paginado/?empleados_filter=';

/**
 * Buscador de empleados con selección múltiple, usado para agregar
 * manualmente empleados a una Justificación Reloj Checador Grupal
 * que no hayan sido detectados por el filtro automático.
 *
 * La selección se acumula en `seleccionados` a través de varias búsquedas
 * (buscar por número, limpiar, buscar por nombre, etc.) hasta que el
 * usuario confirma con "Agregar" o cierra el modal.
 */
const EmpleadoMultiSearch = ({
  open, setVisible, onFinish, excludedIds,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [cargandoArchivo, setCargandoArchivo] = useState(false);

  const handleCargaArchivo = async (file) => {
    setCargandoArchivo(true);
    try {
      const numeros = await extraerNumerosDeArchivo(file);
      if (!numeros.length) {
        message.warning('No se encontraron números de empleado válidos en el archivo.');
        return;
      }

      const encontrados = [];
      const noEncontrados = [];
      const yaEnLista = [];

      for (let i = 0; i < numeros.length; i += LOTE_CONCURRENCIA) {
        const lote = numeros.slice(i, i + LOTE_CONCURRENCIA);
        // eslint-disable-next-line no-await-in-loop
        const respuestas = await Promise.all(lote.map((numero) => (
          getEmpleadosConDatosOpFilteredPaginated(`${baseURIDatosOp}numero_de_empleado=${numero};`)
        )));
        respuestas.forEach((respuesta, idx) => {
          const numero = lote[idx];
          const empleado = respuesta?.results?.[0]?.empleado;
          if (!empleado) {
            noEncontrados.push(numero);
          } else if (excludedIds?.includes(empleado.id)
            || seleccionados.some((emp) => emp.id === empleado.id)
            || encontrados.some((emp) => emp.id === empleado.id)) {
            yaEnLista.push(numero);
          } else {
            encontrados.push(empleado);
          }
        });
      }

      if (encontrados.length) {
        setSeleccionados((prev) => [
          ...prev,
          ...encontrados.filter((emp) => !prev.some((p) => p.id === emp.id)),
        ]);
      }

      const partes = [];
      if (encontrados.length) partes.push(`${encontrados.length} empleado(s) agregado(s)`);
      if (yaEnLista.length) partes.push(`${yaEnLista.length} ya estaba(n) en la lista`);
      if (noEncontrados.length) {
        partes.push(`${noEncontrados.length} no encontrado(s): ${noEncontrados.join(', ')}`);
      }
      const texto = partes.join(' — ') || 'No se agregó ningún empleado nuevo.';
      if (noEncontrados.length) {
        message.warning(texto);
      } else {
        message.success(texto);
      }
    } catch (err) {
      message.error('No se pudo procesar el archivo. Verifica que sea un CSV o XLSX válido.');
    } finally {
      setCargandoArchivo(false);
    }
  };

  const search = async () => {
    setLoading(true);
    const numero = form.getFieldValue('numero_de_empleado');
    const nombre = form.getFieldValue('nombre');
    const plaza = form.getFieldValue('plaza');
    const puesto = form.getFieldValue('puesto');
    let uri = baseURIDatosOp;
    if (numero) {
      uri += `numero_de_empleado=${numero};`;
    }
    if (nombre) {
      uri += `nombre=${nombre};`;
    }
    if (plaza) {
      uri += `plaza=${plaza};`;
    }
    if (puesto) {
      uri += `puesto=${puesto};`;
    }
    const response = await getEmpleadosConDatosOpFilteredPaginated(uri);
    setData(response?.results || []);
    form.resetFields();
    setLoading(false);
  };

  const onCancel = () => {
    form.resetFields();
    setData([]);
    setSeleccionados([]);
    setVisible(false);
  };

  const onConfirm = () => {
    onFinish(seleccionados);
    onCancel();
  };

  const seleccionadosIds = seleccionados.map((emp) => emp.id);
  const rows = data.filter((row) => !excludedIds?.includes(row.empleado?.id));

  const toggleEmpleado = (empleado) => {
    if (!empleado) return;
    setSeleccionados((prev) => (
      prev.some((emp) => emp.id === empleado.id)
        ? prev.filter((emp) => emp.id !== empleado.id)
        : [...prev, empleado]
    ));
  };

  const quitarSeleccionado = (id) => {
    setSeleccionados((prev) => prev.filter((emp) => emp.id !== id));
  };

  // El checkbox de cada fila está bloqueado por CSS a propósito (ver
  // index.scss): la selección real ocurre al hacer clic en cualquier
  // parte de la fila.
  const handleOnRowClick = (record) => toggleEmpleado(record.empleado);

  const filaYaSeleccionada = (row) => seleccionadosIds.includes(row.empleado?.id);

  const rowSelection = {
    selectedRowKeys: rows.filter(filaYaSeleccionada).map((row) => row.id),
    type: 'checkbox',
    columnTitle: (
      <Checkbox
        checked={rows.length > 0 && rows.every(filaYaSeleccionada)}
        indeterminate={rows.some(filaYaSeleccionada) && !rows.every(filaYaSeleccionada)}
        onChange={(e) => {
          if (e.target.checked) {
            setSeleccionados((prev) => {
              const nuevos = rows
                .filter((row) => row.empleado && !prev.some((emp) => emp.id === row.empleado.id))
                .map((row) => row.empleado);
              return [...prev, ...nuevos];
            });
          } else {
            const idsEnVista = rows.map((row) => row.empleado?.id);
            setSeleccionados((prev) => prev.filter((emp) => !idsEnVista.includes(emp.id)));
          }
        }}
      />
    ),
    onSelect: (record) => toggleEmpleado(record.empleado),
    onChange: () => {},
  };

  const columns = [
    {
      titleText: 'No. de Empleado',
      dataIndex: 'empleado',
      key: 'empleado_numero',
      width: 150,
      render: (empObject) => empObject?.numero_de_empleado,
    },
    {
      titleText: 'Nombre Completo',
      dataIndex: 'empleado',
      key: 'empleado_nombre',
      width: 350,
      render: (empObject) => [
        empObject?.nombre, empObject?.apellido_paterno, empObject?.apellido_materno,
      ].filter(Boolean).join(' '),
    },
  ];

  return (
    <Modal
      open={open}
      closable={false}
      maskClosable={!loading}
      footer={null}
      width={900}
      title={(
        <FormSubmitControls
          label="Agregar Empleados Manualmente"
          onFinish={onConfirm}
          disableOnFinish={!seleccionados.length}
          onFinishText={`Agregar (${seleccionados.length})`}
          onCancel={onCancel}
          loading={loading}
        />
      )}
      onCancel={onCancel}
      forceRender
    >
      <Spin tip="Buscando..." spinning={loading}>
        <Form layout="vertical" form={form} name="empleado_multi_search">
          <Row gutter={10}>
            <Col xs={24} sm={5}>
              <Form.Item label="No. de Empleado" name="numero_de_empleado">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={5}>
              <Form.Item label="Nombre" name="nombre">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={5}>
              <Form.Item label="Plaza" name="plaza">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={5}>
              <Form.Item label="Puesto" name="puesto">
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={4}>
              <Button onClick={search} icon={<SearchOutlined />} style={{ marginTop: 30 }}>
                Buscar
              </Button>
            </Col>
          </Row>
        </Form>
        <Row style={{ marginBottom: 10 }}>
          <Col xs={24}>
            <div style={{ marginBottom: 6 }}>
              O carga un archivo CSV/XLSX con números de empleado (uno por fila, primera columna)
            </div>
            <Spin spinning={cargandoArchivo}>
              <Uploader
                title=""
                limitMB={5}
                allowedExtensions={['csv', 'xlsx']}
                allowPreview={false}
                dragger
                draggerStyle={{ height: 80, padding: '8px 0' }}
                fullSize
                onUpload={handleCargaArchivo}
              />
            </Spin>
          </Col>
        </Row>
        <Table
          allowColumnSelector={false}
          cols={columns}
          data={rows}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          loading={loading}
          allowExportParam={false}
          allowImportParam={false}
          allowSearch={false}
        />
        {!!seleccionados.length && (
          <Row style={{ marginTop: 10 }}>
            <Col xs={24}>
              <div style={{ fontWeight: 'bold', marginBottom: 6 }}>
                Empleados seleccionados (
                {seleccionados.length}
                ):
              </div>
              {seleccionados.map((emp) => (
                <Tag
                  key={emp.id}
                  closable
                  onClose={() => quitarSeleccionado(emp.id)}
                  style={{ marginBottom: 4 }}
                >
                  {emp.numero_y_nombre_completo
                    || [emp.nombre, emp.apellido_paterno, emp.apellido_materno].filter(Boolean).join(' ')}
                </Tag>
              ))}
            </Col>
          </Row>
        )}
      </Spin>
    </Modal>
  );
};

export default EmpleadoMultiSearch;
